import { authedFetch, UnauthorizedError } from '@apis/auth';

/**
 * Daily Route domain types — FE mirrors of the BFF response shapes
 * (see `core/reports/daily-route.ports.ts` in the spec). These intentionally
 * duplicate the backend domain types: the BFF response is the FE contract,
 * and `core/*` types are not importable from the frontend.
 */

export type DailyRouteSubject =
  | {
      kind: 'tag';
      name: string;
      tagId: string;
      // Opaque tradeId from the project's profession catalog. Resolve to
      // a translated trade name client-side via `useSiteProfessions` —
      // mirrors Trusstor's `translationService.get(tradeId)` pattern.
      professionTradeId: string | null;
      dayHours: number | null;
    }
  | { kind: 'equipment'; name: string; equipmentId: string; type: string | null };

export type PersonEntityType = 'manager' | 'visitor' | 'worker' | 'none';

export interface WorkPeriod {
  startIso: string;
  endIso: string;
}

export interface PersonRef {
  tagId: string;
  name: string;
  professionTradeId: string | null;
  entityType: PersonEntityType;
  /** Per-tag presence intervals within the stop. Empty array → BFF had no
   * snapshot evidence; consumer should fall back to the stop's overall range. */
  workPeriods: WorkPeriod[];
}

export interface DailyRouteResponse {
  subject: DailyRouteSubject;
  stops: DailyRouteStop[];
}

export interface EquipmentRef {
  equipmentId: string;
  name: string;
  type: string | null;
}

export interface DailyRouteStop {
  stopIndex: number;
  floorId: string;
  floorNick: string;
  areaId: string;
  areaNick: string;
  arrivalIso: string;
  departureIso: string;
  durationMinutes: number;
  personnel: PersonRef[];
  equipment: EquipmentRef[];
}

export interface DailyRouteActivity {
  activityId: string;
  name: string;
  professionId: string;
  professionName: string;
  professionColor: string;
  startIso: string;
  endIso: string;
}

export type StopActivitiesResponse = DailyRouteActivity[];

export interface DailyRouteParams {
  siteId: string;
  entityKind: 'tag' | 'equipment';
  entityId: string;
  /** YYYY-MM-DD in project tz. */
  date: string;
}

export interface StopActivitiesParams {
  siteId: string;
  floorId: string;
  /** YYYY-MM-DD in project tz. */
  date: string;
}

async function handle(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  // 401 reaches us only after authedFetch's refresh-and-retry has already
  // failed and dispatched `dorca:auth-expired`; we just surface the error
  // type so React Query lands in an error state until the redirect fires.
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`request failed: ${res.status} ${body?.error?.message ?? ''}`);
}

/**
 * Fetches the daily route report for a single subject (tag or equipment) on
 * the given date. The BFF reconciles the Trusstor route + subject upstream
 * calls and returns a unified `{ subject, stops }` shape.
 */
export async function fetchDailyRoute(
  params: DailyRouteParams,
  signal?: AbortSignal,
): Promise<DailyRouteResponse> {
  const qs = new URLSearchParams({
    entityKind: params.entityKind,
    entityId: params.entityId,
    date: params.date,
  });
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(params.siteId)}/reports/daily-route?${qs.toString()}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as DailyRouteResponse;
}

/**
 * Fetches the activities scheduled on a given floor for the given date.
 * Used to render the per-stop "activities on this floor" section. Returns
 * a plain array (the BFF flattens the response).
 */
export async function fetchStopActivities(
  params: StopActivitiesParams,
  signal?: AbortSignal,
): Promise<StopActivitiesResponse> {
  const qs = new URLSearchParams({
    floorId: params.floorId,
    date: params.date,
  });
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(params.siteId)}/reports/daily-route/activities?${qs.toString()}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as StopActivitiesResponse;
}

// ─── Incident Report ────────────────────────────────────────────────────

export interface IncidentReportResponse {
  subject: DailyRouteSubject;
  stops: DailyRouteStop[];
  atMs: number;
  windowStartMs: number;
  windowEndMs: number;
  /** Stop index containing `atMs`, or `null` if the subject is between
   *  stops at the incident instant. The page uses this to auto-select. */
  atStopIndex: number | null;
}

export interface IncidentReportParams {
  siteId: string;
  entityKind: 'tag' | 'equipment';
  entityId: string;
  /** Incident instant — epoch ms. The BFF computes the `± 30 min` window. */
  atTimestamp: number;
}

export interface IncidentTagSafetyEventsParams {
  siteId: string;
  tagId: string;
  /** Incident instant — epoch ms. BFF centres a 1-hour window on this. */
  atTimestamp: number;
}

/**
 * Subject-card safety events (top chip grid). Filters to a specific tag
 * in a 1-hour window centred on `atTimestamp`, across all areas/floors.
 * Mirrors trusstor's `getSafetyEventsByTagIdInOneHourTimeframe`.
 */
export async function fetchIncidentTagSafetyEvents(
  params: IncidentTagSafetyEventsParams,
  signal?: AbortSignal,
): Promise<SafetyEvent[]> {
  const qs = new URLSearchParams({
    tagId: params.tagId,
    atTimestamp: String(params.atTimestamp),
  });
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(params.siteId)}/reports/incident-report/safety-events?${qs.toString()}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as SafetyEvent[];
}

export async function fetchIncidentReport(
  params: IncidentReportParams,
  signal?: AbortSignal,
): Promise<IncidentReportResponse> {
  const qs = new URLSearchParams({
    entityKind: params.entityKind,
    entityId: params.entityId,
    atTimestamp: String(params.atTimestamp),
  });
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(params.siteId)}/reports/incident-report?${qs.toString()}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as IncidentReportResponse;
}

// ─── Observe Location ───────────────────────────────────────────────────

export interface ObserveLocationWorkPeriod {
  startIso: string;
  endIso: string;
}

export interface ObserveLocationPersonnelPeriods {
  tagId: string;
  tagNick: string;
  name: string | null;
  professionTradeId: string | null;
  /**
   * Trade background color resolved adapter-side from
   * `listProfessions(projectId)`. Null for utility/manager tags or
   * unmapped trades. Used to tint the personnel timeline period bars.
   */
  professionColor: string | null;
  totalMs: number;
  workPeriods: ObserveLocationWorkPeriod[];
}

export interface ObserveLocationEquipmentRef {
  equipmentId: string;
  name: string;
  typeNick: string | null;
  type: string | null;
  company: string | null;
}

export interface ObserveLocationVisits {
  firstEnteredIso: string | null;
  lastLeftIso: string | null;
  busiestTime: { startIso: string; endIso: string } | null;
  histogram: Record<string, string[]>;
}

export interface ObserveLocationResponse {
  personnel: ObserveLocationPersonnelPeriods[];
  equipment: ObserveLocationEquipmentRef[];
  visits: ObserveLocationVisits;
}

export interface ObserveLocationParams {
  siteId: string;
  areasIds: string[];
  /** YYYY-MM-DD in project tz. */
  date: string;
}

export async function fetchObserveLocation(
  params: ObserveLocationParams,
  signal?: AbortSignal,
): Promise<ObserveLocationResponse> {
  const qs = new URLSearchParams({
    areasIds: params.areasIds.join(','),
    date: params.date,
  });
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(params.siteId)}/reports/observe-location?${qs.toString()}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as ObserveLocationResponse;
}

// ─── Project Overview ───────────────────────────────────────────────────

/**
 * Project Overview domain types — FE mirrors of the BFF response shapes,
 * which in turn mirror Trusstor's reports-engine wire shapes field-for-field
 * (`IProductivityReport`, `IBackendActivitiesReport`, `ISafetyReport`) so the
 * rendering + rounding logic ports verbatim. The single exception is
 * `profession`, normalised adapter-side to a flat snapshot with
 * `backgroundColor` already resolved (ADR-007 — upstream types stop at the
 * adapter). The BFF returns the *raw* (unrounded) report; the FE applies
 * `getRoundedProductivityReport` / `getRoundedActivityReport` (see
 * `Reports/ProjectOverview/rounding.ts`), matching Trusstor's store topology.
 */

/** `DATE_PICKER_TOGGLE` value (uppercase) — daily vs multi-day mode. */
export type ProjectOverviewMode = 'DATE' | 'PERIOD';

/** Flat profession snapshot resolved by the BFF adapter (D5). */
export interface ProjectOverviewProfession {
  _id: string;
  tradeId: string;
  tradeGroup?: string | null;
  specialty?: string | null;
  contractor?: string | null;
  /** Resolved server-side per project; used directly for all coloring. */
  backgroundColor: string;
  sortIndex?: number;
}

export interface ProductivityAreaReport {
  areaId: string;
  areaNick: string;
  floorId: string;
  tagIds: string[];
  totalTimeMS: number;
}

export interface BaseProductivityFloorReport {
  floorId: string;
  floorNick: string;
  shortFloorNick: string;
  totalHours: number;
}

export interface DailyProductivityFloorReport extends BaseProductivityFloorReport {
  tagIds: string[];
}

export interface PeriodicProductivityFloorReport extends BaseProductivityFloorReport {
  uniqueTagIdsPerDay: Record<string, string[]>;
  totalDays: number;
}

export type ProductivityFloorReport =
  | DailyProductivityFloorReport
  | PeriodicProductivityFloorReport;

export interface ProductivityProfessionReport {
  profession: ProjectOverviewProfession;
  floors: Record<string, ProductivityFloorReport>;
  areas: Record<string, ProductivityAreaReport>;
  totalTimeOnSiteMS?: number;
}

export type ProductivityReport = Record<string, ProductivityProfessionReport>;

export interface ActivityManager {
  /** Manager document id — the managers-filter join key (Trusstor matches
   *  `activity.manager?._id` against the selected managers). */
  _id?: string;
  name: string;
  backgroundColor: string;
}

export interface ActivityReport {
  _id: string;
  startDate: string;
  endDate: string;
  description: string;
  floor: { floorId: string; floorNick: string };
  manager?: ActivityManager | null;
  accumulatedWorkTime: number;
  avgNumOfWorkersPerDay: number;
  avgWorkHoursPerDay: number;
  activitiesGroupStatus?: string;
  tags?: string[];
  totalTagIds?: string[];
  uniqueTagIdsPerDay?: Record<string, string[]>;
}

export interface ActivitiesFloorReport {
  floorId: string;
  floorNick: string;
  shortFloorNick?: string;
  activities: ActivityReport[];
}

export interface ActivitiesProfessionReport {
  profession: ProjectOverviewProfession;
  floors: Record<string, ActivitiesFloorReport>;
}

export type ActivitiesReport = Record<string, ActivitiesProfessionReport>;

export interface SafetyEventRaw {
  eventId: string;
  type: string;
  timestampStart: number;
  timestampEnd: number | null;
  isCritical: boolean;
  areas?: { areaId: string; areaNick: string }[];
}

export interface SafetyFloorReport {
  floorId: string;
  floorNick: string;
  shortFloorNick?: string;
  events: SafetyEventRaw[];
}

export interface SafetyProfessionReport {
  profession: ProjectOverviewProfession;
  floors: Record<string, SafetyFloorReport>;
}

export type SafetyReport = Record<string, SafetyProfessionReport>;

export interface ProjectOverviewParams {
  siteId: string;
  mode: ProjectOverviewMode;
  /** YYYY-MM-DD in project tz. */
  dateFrom: string;
  /** YYYY-MM-DD in project tz — required iff mode === 'PERIOD'. */
  dateTo?: string;
}

function projectOverviewQuery(params: ProjectOverviewParams): string {
  const qs = new URLSearchParams({ mode: params.mode, dateFrom: params.dateFrom });
  if (params.dateTo) qs.set('dateTo', params.dateTo);
  return qs.toString();
}

async function fetchProjectOverviewSlice<T>(
  slice: 'productivity' | 'activities' | 'safety',
  params: ProjectOverviewParams,
  signal?: AbortSignal,
): Promise<T> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(params.siteId)}/reports/project-overview/${slice}?${projectOverviewQuery(params)}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  const body = (await res.json()) as { report: T };
  return body.report;
}

/** Work Resources Analysis slice (raw productivity report). */
export function fetchProjectOverviewProductivity(
  params: ProjectOverviewParams,
  signal?: AbortSignal,
): Promise<ProductivityReport> {
  return fetchProjectOverviewSlice<ProductivityReport>('productivity', params, signal);
}

/** Activities slice (raw backend activities report). */
export function fetchProjectOverviewActivities(
  params: ProjectOverviewParams,
  signal?: AbortSignal,
): Promise<ActivitiesReport> {
  return fetchProjectOverviewSlice<ActivitiesReport>('activities', params, signal);
}

/** Safety Alerts slice (raw safety report, keyed by profession → floor). */
export function fetchProjectOverviewSafety(
  params: ProjectOverviewParams,
  signal?: AbortSignal,
): Promise<SafetyReport> {
  return fetchProjectOverviewSlice<SafetyReport>('safety', params, signal);
}

/**
 * Manager entry for the in-page managers filter. Mirrors the BFF's
 * `ProjectOverviewManagerSchema` (backend/src/api/schemas/reports.schemas.ts)
 * — the backend adapter strips PII (e.g. phone number) and unmapped upstream
 * fields before this shape ever reaches the frontend.
 */
export interface ProjectOverviewManager {
  _id: string;
  name: string;
  tradeId: string;
  tradeGroup?: string;
  backgroundColor: string;
  sortIndex?: number;
}

export interface ProjectOverviewManagersParams {
  siteId: string;
  /** YYYY-MM-DD in project tz — managers active from this date. */
  dateFrom: string;
}

/** Managers roster for the in-page filter rail; the BFF responds with `{ managers: [...] }`. */
export async function fetchProjectOverviewManagers(
  params: ProjectOverviewManagersParams,
  signal?: AbortSignal,
): Promise<ProjectOverviewManager[]> {
  const qs = new URLSearchParams({ dateFrom: params.dateFrom });
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(params.siteId)}/reports/project-overview/managers?${qs.toString()}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  const body = (await res.json()) as { managers: ProjectOverviewManager[] };
  return body.managers;
}

// ─── Safety Events (range) ──────────────────────────────────────────────

/**
 * "Who is this event about", as resolved by the BFF adapter. Discriminated
 * so visitor-class events (which have no upstream name) can travel as an
 * i18n key instead of an English literal, letting the FE localise via `t()`.
 */
export type SafetyEventSubject =
  | { kind: 'named'; name: string }
  | { kind: 'i18n'; key: string };

export interface SafetyEvent {
  safetyEventId: string;
  safetyViolationType: string;
  isCritical: boolean;
  timestamp: string;
  trade: { tradeId: string; name: string; backgroundColor: string | null } | null;
  /** See {@link SafetyEventSubject}. `null` when the upstream payload had
   *  no usable identity (no named subject and no category fallback). */
  subject: SafetyEventSubject | null;
  areaId: string | null;
  floorId: string | null;
}

/**
 * Returns a copy of `events` sorted by `timestamp` ascending (oldest first).
 * Used as the `select` transform in every safety-events React Query hook so
 * the upstream's ordering never leaks to the UI — the IncidentSummaryCard
 * chip grid, the per-stop SafetyEventsSection, and the ObserveLocation
 * SafetyEventsPanel all read events in the same chronological order.
 *
 * Stable on ties: events sharing a `timestamp` keep their relative input
 * order (Array.prototype.sort is stable in V8/JSC since 2019).
 */
export function sortSafetyEventsByTimestamp(
  events: SafetyEvent[],
): SafetyEvent[] {
  return [...events].sort(
    (a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp),
  );
}

export interface RangeSafetyEventsParams {
  siteId: string;
  startTimestamp: number;
  endTimestamp: number;
  areaId?: string;
  floorId?: string;
}

export async function fetchRangeSafetyEvents(
  params: RangeSafetyEventsParams,
  signal?: AbortSignal,
): Promise<SafetyEvent[]> {
  const qs = new URLSearchParams({
    startTimestamp: String(params.startTimestamp),
    endTimestamp: String(params.endTimestamp),
  });
  if (params.areaId) qs.set('areaId', params.areaId);
  if (params.floorId) qs.set('floorId', params.floorId);
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(params.siteId)}/reports/observe-location/safety-events?${qs.toString()}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as SafetyEvent[];
}
